import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/shared/api/client";
import { type DeviceListKind, queryKeys } from "@/shared/api/query-keys";
import type { DeviceDto } from "@/shared/api/types";
import { useTenantId } from "@/shared/auth/session";

export interface DevicesQueryOptions {
  /**
   * M24 (contracts §10 S1, C2 §1): `terminal` (the default — Dahua terminals only, what every consumer
   * read before Face Kiosk), `face_kiosk` (seats only) or `all`. Only the consumers that must draw a
   * seat pass `all`: the terminal fields, the Add-person picker, the Events and report filters, the
   * Overview seats branch and the Devices page usage line. Everything that counts "terminals online"
   * keeps `terminal`, because a seat is never online in the Dahua sense.
   */
  kind?: DeviceListKind;
}

/**
 * The devices list is shared by the shell (live count), overview, events filters, the devices page and
 * the terminal picker on Add person. `enabled: false` keeps a role without `devices:view` from asking
 * for what the api would refuse (rules/01 §10).
 */
export function useDevicesQuery(enabled = true, { kind = "terminal" }: DevicesQueryOptions = {}) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: queryKeys.tenant.devices.list(tenantId, kind),
    // The api's default is `terminal`: that request stays byte-for-byte what it was before M24.
    queryFn: ({ signal }) =>
      apiGet<DeviceDto[]>("/devices", kind === "terminal" ? undefined : { kind }, signal),
    staleTime: 15_000,
    enabled,
  });
}

/** A Face Kiosk seat row of `GET /devices?kind=face_kiosk|all` (M24). */
export function isFaceKioskSeat(device: Pick<DeviceDto, "kind">): boolean {
  return device.kind === "face_kiosk";
}
